import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import WantDemandBadge from '../components/WantDemandBadge';
import { useMyWants } from '../hooks/useMyWants';
import ActiveTraderBadge from '../components/ActiveTraderBadge';
import ListingThumb from '../components/ListingThumb';
import PedalPriceDisplay from '../components/PedalPriceDisplay';
import { EFFECT_TYPES } from '../utils/effectTypes';
import { listingImageUrls, isPlaceholderListing, placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { OFFER_FILTER_OPTIONS, matchesOfferFilter, offerModeOf } from '../utils/offerMode';

const CONDITIONS = ['New', 'Excellent', 'Very Good', 'Good', 'Poor', 'Damaged'];
const DECADES = ['1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];
const SEARCH_STORAGE_KEY = 'searchResults_scrollState';

const SENTINEL_ROOT_MARGIN = '1500px 0px';
const SUPPORTS_INTERSECTION_OBSERVER =
  typeof window !== 'undefined' && 'IntersectionObserver' in window;

// Apply filter logic to results (shared between restoration and filter effect)
const applyFilters = (results, fET, fPrice, fCondition, fDecade, fOffer = '') => {
  let filtered = [...results];
  if (fET.length > 0) filtered = filtered.filter(r => r.effectTypes && r.effectTypes.some(t => fET.includes(t)));
  if (fPrice.min !== '') filtered = filtered.filter(r => Number(r.price) >= Number(fPrice.min));
  if (fPrice.max !== '') filtered = filtered.filter(r => Number(r.price) <= Number(fPrice.max));
  if (fCondition) {
    const minIdx = CONDITIONS.indexOf(fCondition);
    filtered = filtered.filter(r => {
      const idx = CONDITIONS.indexOf(r.condition);
      return idx !== -1 && idx <= minIdx;
    });
  }
  if (fDecade) filtered = filtered.filter(r => r.decade === fDecade);
  if (fOffer) filtered = filtered.filter(r => matchesOfferFilter(r, [fOffer]));
  return filtered;
};

const SearchResults = () => {
  const currentUser = auth.currentUser;
  const { firestoreUser } = useContext(UserContext);
  // One want-list listener for every heart badge on the page (never per card).
  const { wants: myWants } = useMyWants(currentUser);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q')?.trim() || '';
  const [allResults, setAllResults] = useState([]);
  const [displayedResults, setDisplayedResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);

  // Synchronous session read for filter initialization
  const s = (() => {
    try {
      const raw = sessionStorage.getItem(SEARCH_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > 30 * 60 * 1000) return null;
      if (parsed.searchQuery !== (new URLSearchParams(window.location.search).get('q')?.trim() || '')) return null;
      return parsed;
    } catch { return null; }
  })();

  const [filterEffectTypes, setFilterEffectTypes] = useState(s?.filterEffectTypes || []);
  const [filterPrice, setFilterPrice] = useState({ min: s?.filterPrice?.min || '', max: s?.filterPrice?.max || '' });
  const [filterCondition, setFilterCondition] = useState(s?.filterCondition || '');
  const [filterDecade, setFilterDecade] = useState(s?.filterDecade || '');
  const [filterOffer, setFilterOffer] = useState(s?.filterOffer || ''); // '' | 'Trade' | 'Buy' (plan §5)

  const activeFilterCount =
    (filterEffectTypes.length > 0 ? 1 : 0) +
    (filterCondition !== '' ? 1 : 0) +
    (filterDecade !== '' ? 1 : 0) +
    (filterOffer !== '' ? 1 : 0) +
    ((filterPrice.min !== '' || filterPrice.max !== '') ? 1 : 0);

  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isFilterSheetClosing, setIsFilterSheetClosing] = useState(false);
  const [filterSubPage, setFilterSubPage] = useState(null);
  const FILTER_SHEET_ANIM_MS = 220;

  const closeFilterSheet = () => {
    if (!isFilterSheetOpen || isFilterSheetClosing) return;
    setIsFilterSheetClosing(true);
    setTimeout(() => {
      setIsFilterSheetOpen(false);
      setIsFilterSheetClosing(false);
      setFilterSubPage(null);
    }, FILTER_SHEET_ANIM_MS);
  };

  const openFilterSheet = () => {
    setIsFilterSheetClosing(false);
    setIsFilterSheetOpen(true);
    setFilterSubPage(null);
  };

  const [loading, setLoading] = useState(true);
  const [availableMakes, setAvailableMakes] = useState([]);
  const [displayCount, setDisplayCount] = useState(28);
  const [hasMore, setHasMore] = useState(false);
  const isRestoringState = useRef(false);
  const sentinelRef = useRef(null);

  /* Sticky header compaction: when the user scrolls past the threshold the
     expanded title/copy/filter-button block is overlaid by a slim compact
     bar anchored to the top of the viewport. Mirrors the Recommendations /
     Wantlist Matches / Latest Pedals pattern — same thresholds (mobile 550,
     desktop 400). The compact bar here only houses a Filter button (no
     search/toggle for this page). Initial value is computed from
     window.scrollY at mount so a sessionStorage scroll restore lands with
     the correct compact state — the scroll listener only fires on user
     scroll events, so without this seed the bar would stay expanded until
     the next interaction. */
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.scrollY > (window.innerWidth <= 768 ? 550 : 400);
  });

  useEffect(() => {
    const handleAddOpened = () => { if (isFilterSheetOpen) closeFilterSheet(); };
    window.addEventListener('bottomTabBarAddOpened', handleAddOpened);
    return () => window.removeEventListener('bottomTabBarAddOpened', handleAddOpened);
  }, [isFilterSheetOpen]);

  // Scroll listener — drives the sticky header compaction. One passive
  // listener, single read, threshold tuned so the compact bar only appears
  // after the expanded hero (title + description + filter row)
  // has scrolled off-viewport. Mirrors the same pattern in
  // RecommendationsPage / WantListMatchesPage / LatestPedals.
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      setIsHeaderCompact(y > (isMobile ? 550 : 400));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Save state to sessionStorage before navigating to a listing
  const saveScrollState = () => {
    const state = {
      allResults,
      filterEffectTypes,
      filterPrice,
      filterCondition,
      filterDecade,
      filterOffer,
      displayCount,
      availableMakes,
      scrollY: window.scrollY,
      searchQuery,
      timestamp: Date.now()
    };
    sessionStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(state));
  };

  // Save state on beforeunload so unexpected reloads (browser refresh,
  // cross-tab Firebase reinit, navigation away via non-Link means) still
  // restore the user's position when they return. Mirrors LatestPedals.
  // Guarded on `allResults.length` so we don't overwrite a valid saved
  // snapshot with an empty one during the initial load-before-fetch
  // window.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (allResults.length > 0) {
        saveScrollState();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allResults, filterEffectTypes, filterPrice, filterCondition, filterDecade, filterOffer, displayCount, availableMakes, searchQuery]);

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem(SEARCH_STORAGE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        const age = Date.now() - state.timestamp;

        // Only restore if same query, state is less than 30 min old
        if (age < 30 * 60 * 1000 && state.searchQuery === searchQuery) {
          isRestoringState.current = true;
          const restoredResults = state.allResults || [];
          const restoredFET = state.filterEffectTypes || [];
          const restoredFP = state.filterPrice || { min: '', max: '' };
          const restoredFC = state.filterCondition || '';
          const restoredFD = state.filterDecade || '';
          const restoredFO = state.filterOffer || '';
          const restoredDisplayCount = state.displayCount || 28;

          // Compute derived state synchronously so content renders immediately
          const restoredFiltered = applyFilters(restoredResults, restoredFET, restoredFP, restoredFC, restoredFD, restoredFO);
          const restoredDisplayed = restoredFiltered.slice(0, restoredDisplayCount);

          setAllResults(restoredResults);
          setFilterEffectTypes(restoredFET);
          setFilterPrice(restoredFP);
          setFilterCondition(restoredFC);
          setFilterDecade(restoredFD);
          setFilterOffer(restoredFO);
          setDisplayCount(restoredDisplayCount);
          setFilteredResults(restoredFiltered);
          setDisplayedResults(restoredDisplayed);
          setHasMore(restoredFiltered.length > restoredDisplayCount);
          setAvailableMakes(state.availableMakes || []);
          setLoading(false);

          // Restore scroll position after render
          setTimeout(() => {
            const restoredY = state.scrollY || 0;
            window.scrollTo(0, restoredY);
            // Sync compact-header state to the restored scroll position.
            // The scroll listener only fires on real scroll events, so
            // without this explicit set the compact bar would stay hidden
            // until the user scrolled again — even if the restored
            // position is past the threshold.
            const isMobile = window.innerWidth <= 768;
            setIsHeaderCompact(restoredY > (isMobile ? 550 : 400));
            setTimeout(() => {
              isRestoringState.current = false;
            }, 200);
          }, 100);
          return; // Don't fetch fresh data
        } else {
          sessionStorage.removeItem(SEARCH_STORAGE_KEY);
        }
      } catch (error) {
        console.error('Error restoring search state:', error);
        sessionStorage.removeItem(SEARCH_STORAGE_KEY);
      }
    }
    isRestoringState.current = false;
  }, []); // Only run on mount

  // Clear saved state when navigating away from this page (except to listings)
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname;
      const isGoingToListing = currentPath.startsWith('/listing/');
      const isStillOnSearch = currentPath.startsWith('/search');
      // Don't clear if navigating to a listing (user may click back)
      // Don't clear if still on search page (handles React StrictMode double-mount)
      if (!isGoingToListing && !isStillOnSearch) {
        sessionStorage.removeItem(SEARCH_STORAGE_KEY);
      }
    };
  }, []);

  useEffect(() => {
    // Skip fetching if we just restored state from sessionStorage
    if (isRestoringState.current) return;

    const fetchResults = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'listings'),
          where('active', '==', true),
          orderBy('listedAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const results = [];
        const ownerIds = new Set();
        // Normalize brand names to avoid duplicates (e.g., "electro-harmonix" vs "Electro-Harmonix")
        const normalizeBrandName = (brand) => {
          if (!brand) return '';
          return brand.toLowerCase().trim();
        };

        const brandMap = new Map();
        const normalize = (str) => str ? str.toLowerCase().replace(/['''\u2019\-]/g, '') : '';
        const normalizedQuery = normalize(searchQuery.trim());
        const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
        const allWordsMatch = (str) => {
          const normalizedStr = normalize(str);
          return queryWords.every(word => normalizedStr.includes(word));
        };
        const searchLower = searchQuery.trim().toLowerCase();
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          if (data.inTrade) continue;
          const combinedName = `${data.make || ''} ${data.model || ''}`.toLowerCase();
          const matchesSearch =
            combinedName.includes(searchLower) ||
            normalize(combinedName).includes(normalizedQuery) ||
            allWordsMatch(combinedName) ||
            data.make?.toLowerCase().includes(searchLower) ||
            normalize(data.make).includes(normalizedQuery) ||
            data.model?.toLowerCase().includes(searchLower) ||
            normalize(data.model).includes(normalizedQuery) ||
            (data.effectTypes && Array.isArray(data.effectTypes) &&
              data.effectTypes.some(type =>
                type.toLowerCase().includes(searchLower) ||
                normalize(type).includes(normalizedQuery)
              ));
          if (!matchesSearch) continue;
          results.push({ id: docSnap.id, ...data });
          ownerIds.add(data.ownerId);
          if (data.make) {
            const normalized = normalizeBrandName(data.make);
            if (normalized && !brandMap.has(normalized)) {
              // Store the original capitalization for display
              brandMap.set(normalized, data.make);
            }
          }
        }
        
        // Fetch owner data for all unique owner IDs
        const userData = {};
        if (ownerIds.size > 0) {
          const userFetches = Array.from(ownerIds).map(async (ownerId) => {
            try {
              // PII isolation: read sanitized public mirror.
              const ownerDoc = await getDoc(doc(db, 'publicProfiles', ownerId));
              if (ownerDoc.exists()) {
                userData[ownerId] = ownerDoc.data();
              } else {
                userData[ownerId] = { username: 'User', completedTrades: 0, lastActiveWeek: null };
              }
            } catch (err) {
              userData[ownerId] = { username: 'User', completedTrades: 0, lastActiveWeek: null };
            }
          });
          await Promise.all(userFetches);
        }
        
        // Enrich results with owner data
        const enrichedResults = results.map(pedal => ({
          ...pedal,
          owner: {
            username: (userData[pedal.ownerId] || {})?.username || 'User',
            badge: (userData[pedal.ownerId] || {})?.completedTrades || 0,
            // lastActiveAt is no longer mirrored — the badge utility prefers lastActiveWeek.
            lastActiveWeek: (userData[pedal.ownerId] || {})?.lastActiveWeek || null,
            responderBadge: (userData[pedal.ownerId] || {})?.responderBadge || null
          }
        }));
        
        setAllResults(enrichedResults);
        // Only show brands that are present in the search results
        setAvailableMakes(Array.from(brandMap.values()).sort());
        setLoading(false);
      } catch (err) {
        setAllResults([]);
        setAvailableMakes([]);
        setLoading(false);
      }
    };
    if (searchQuery) fetchResults();
    else {
      setAllResults([]);
      setAvailableMakes([]);
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    // Skip during restoration — derived state was already set directly
    if (isRestoringState.current) return;

    const filtered = applyFilters(allResults, filterEffectTypes, filterPrice, filterCondition, filterDecade, filterOffer);
    setFilteredResults(filtered);
    setDisplayCount(28);
  }, [allResults, filterEffectTypes, filterPrice, filterCondition, filterDecade, filterOffer]);

  // Handle pagination display
  useEffect(() => {
    // Skip during restoration — derived state was already set directly
    if (isRestoringState.current) return;

    const displayed = filteredResults.slice(0, displayCount);
    setDisplayedResults(displayed);
    setHasMore(filteredResults.length > displayCount);
  }, [filteredResults, displayCount]);

  const loadMoreResults = () => {
    setDisplayCount(prev => prev + 28);
  };

  useEffect(() => {
    if (!SUPPORTS_INTERSECTION_OBSERVER) return;
    if (!sentinelRef.current) return;
    if (!hasMore) return;
    if (loading) return;
    if (displayedResults.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (isRestoringState.current) return;
        loadMoreResults();
      },
      { rootMargin: SENTINEL_ROOT_MARGIN }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, displayedResults.length, filteredResults.length]);

  return (
    <div className="min-h-screen search-results-page" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
      <style jsx>{`
        .search-filter-grid {
          grid-template-columns: 1fr 1fr 1fr;
        }
        
        @media (max-width: 768px) {
          .search-results-page {
            padding: 0 0.5rem !important;
          }
          .search-filter-wrapper {
            padding: 0 !important;
          }
          .search-filter-box {
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding: 1rem !important;
            width: 100% !important;
          }
          .search-filter-grid {
            grid-template-columns: 1fr !important;
            column-gap: 0 !important;
            row-gap: 0.15rem !important;
            padding: 0 !important;
            margin-top: 0.75rem !important;
          }
          .search-filter-grid label {
            font-weight: 600 !important;
            font-size: 0.9rem !important;
            margin-top: 0.5rem !important;
          }
          .search-filter-grid input,
          .search-filter-grid select {
            width: 100% !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>
      <div style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        {/* Compact sticky bar — overlays the top of the viewport when the user
            scrolls past the expanded hero (controlled by isHeaderCompact).
            Houses only the Filter button on this page (no search/toggle —
            the search query is already locked in via the URL). Same
            overlay-not-collapse pattern as Recommendations / Wantlist
            Matches: the expanded header below stays in normal page flow so
            we never shift layout under the user's finger on mobile. */}
        <div className={`search-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
          <style jsx>{`
            .search-sticky-header .search-compact-bar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              z-index: 100;
              padding: 0.6rem 1rem 0.7rem 1rem;
              background-color: #F7F3EB;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
              box-sizing: border-box;
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s ease;
              display: flex;
              justify-content: flex-end;
              align-items: center;
            }
            .search-sticky-header.is-compact .search-compact-bar {
              opacity: 1;
              pointer-events: auto;
            }
            @media (min-width: 769px) {
              .search-sticky-header .search-header-expanded {
                text-align: left !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              .search-sticky-header .search-header-expanded-inner {
                max-width: 1600px;
                width: 100%;
                margin-left: auto;
                margin-right: auto;
                padding-left: 2rem;
                padding-right: 2rem;
                box-sizing: border-box;
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                gap: 0.5rem 5rem;
              }
              .search-sticky-header .search-header-copy {
                flex: 0 1 auto;
                min-width: 0;
              }
              .search-sticky-header .search-header-controls {
                flex: 0 0 auto;
                margin-left: auto;
                margin-right: 1rem;
                display: flex;
                align-items: center;
                gap: 0.75rem;
                align-self: flex-end;
              }
              .search-sticky-header .search-header-expanded h1 {
                text-align: left !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .search-sticky-header .search-header-description {
                margin: 0 !important;
                padding: 0 !important;
                max-width: none !important;
                text-align: left !important;
              }
              @media (max-width: 900px) {
                .search-sticky-header .search-header-expanded-inner {
                  flex-direction: column;
                  gap: 1rem;
                }
                .search-sticky-header .search-header-controls {
                  margin-left: 0;
                  margin-right: 0;
                  align-self: stretch;
                  justify-content: flex-end;
                }
              }
            }
            @media (max-width: 768px) {
              .search-compact-filter-btn {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>
          <div className="search-compact-bar">
            <button
              className="search-compact-filter-btn"
              onClick={openFilterSheet}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '0.6rem 1.4rem',
                borderRadius: '9999px',
                border: '1px solid #C89F3C',
                backgroundColor: activeFilterCount > 0 ? '#C89F3C' : 'white',
                color: activeFilterCount > 0 ? 'white' : '#374151',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              aria-label="Open filters"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
              </svg>
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>

          <div className="search-header-expanded mb-10" style={{ textAlign: 'center', marginBottom: '2rem', padding: '0 1.5rem' }}>
            <div className="search-header-expanded-inner">
              <div className="search-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Search Results
                </h1>
                <p className="search-header-description text-gray-600" style={{ fontSize: '17px' }}>
                  Showing results for <span className="font-semibold">"{searchQuery}"</span>
                </p>
              </div>
              <div className="search-header-controls">
                <button
                  onClick={openFilterSheet}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '30px',
                    border: '1px solid #C89F3C',
                    backgroundColor: activeFilterCount > 0 ? '#C89F3C' : 'white',
                    color: activeFilterCount > 0 ? 'white' : '#374151',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '700',
                    fontSize: '1.125rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
                  </svg>
                  Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filter panel (desktop slide-in) */}
        {isFilterSheetOpen && (() => {
          // Shared list: src/utils/effectTypes.js


          const chipStyle = (selected) => ({
            padding: '8px 18px',
            borderRadius: '9999px',
            border: selected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
            backgroundColor: selected ? 'rgba(201, 162, 39, 0.12)' : 'white',
            color: selected ? '#946F00' : '#374151',
            fontWeight: selected ? '700' : '500',
            fontSize: '0.9rem',
            fontFamily: 'Nunito, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          });

          const rowStyle = {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '1rem 1.25rem',
            cursor: 'pointer',
            borderBottom: '1px solid #f3f4f6'
          };

          const badgeArr = (arr) => arr.length > 0
            ? <span style={{ fontSize: '0.85rem', color: '#C89F3C', fontWeight: '700' }}>({arr.length})</span>
            : null;
          const badgeVal = (val) => val
            ? <span style={{ fontSize: '0.85rem', color: '#C89F3C', fontWeight: '700' }}>(1)</span>
            : null;

          const chevronRight = (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          );
          const chevronLeft = (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          );

          const renderMainPage = () => (
            <div style={{ fontFamily: 'Nunito, sans-serif' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#374151' }}>Filters</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => { setFilterEffectTypes([]); setFilterCondition(''); setFilterDecade(''); setFilterPrice({ min: '', max: '' }); }}
                      style={{ background: 'none', border: 'none', color: '#C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                    >
                      Clear all
                    </button>
                  )}
                  <button onClick={closeFilterSheet} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: '700', fontSize: '0.95rem', color: '#5A9ECB' }}>Price Range</p>
                <div style={{ display: 'flex', gap: '0.75rem', width: '100%', boxSizing: 'border-box', padding: '0 1rem' }}>
                  <input
                    type="number"
                    placeholder="Min $"
                    value={filterPrice.min}
                    onChange={e => setFilterPrice(p => ({ ...p, min: e.target.value }))}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem 1rem', borderRadius: '25px', border: '1px solid #C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none' }}
                  />
                  <input
                    type="number"
                    placeholder="Max $"
                    value={filterPrice.max}
                    onChange={e => setFilterPrice(p => ({ ...p, max: e.target.value }))}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem 1rem', borderRadius: '25px', border: '1px solid #C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none' }}
                  />
                </div>
              </div>
              <div style={rowStyle} onClick={() => setFilterSubPage('effectTypes')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Effect Type {badgeArr(filterEffectTypes)}</span>
                {chevronRight}
              </div>
              <div style={rowStyle} onClick={() => setFilterSubPage('condition')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Condition {badgeVal(filterCondition)}</span>
                {chevronRight}
              </div>
              <div style={rowStyle} onClick={() => setFilterSubPage('decade')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Decade {badgeVal(filterDecade)}</span>
                {chevronRight}
              </div>
              <div style={{ ...rowStyle, borderBottom: 'none' }} onClick={() => setFilterSubPage('offer')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Offers {badgeVal(filterOffer)}</span>
                {chevronRight}
              </div>
              <div style={{ padding: '1rem 1.25rem' }}>
                <button
                  onClick={closeFilterSheet}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '25px', border: 'none', backgroundColor: '#C89F3C', color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          );

          const renderSubPage = () => {
            const isET = filterSubPage === 'effectTypes';
            const isCond = filterSubPage === 'condition';
            const isOff = filterSubPage === 'offer';
            const title = isET ? 'Effect Type' : isCond ? 'Condition' : isOff ? 'Offers' : 'Decade';
            const options = isET ? EFFECT_TYPES : isCond ? CONDITIONS : isOff ? OFFER_FILTER_OPTIONS : DECADES;

            if (isET) {
              return (
                <div style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' }}>
                    {chevronLeft}
                    <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>{title}</span>
                  </button>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
                    {options.map(opt => {
                      const on = filterEffectTypes.includes(opt);
                      return (
                        <button key={opt} type="button" onClick={() => setFilterEffectTypes(prev => on ? prev.filter(x => x !== opt) : [...prev, opt])} style={chipStyle(on)}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }

            const selected = isCond ? filterCondition : isOff ? filterOffer : filterDecade;
            const setSelected = isCond ? setFilterCondition : isOff ? setFilterOffer : setFilterDecade;
            return (
              <div style={{ fontFamily: 'Nunito, sans-serif' }}>
                <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' }}>
                  {chevronLeft}
                  <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>{title}</span>
                </button>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
                  {options.map(opt => {
                    const on = selected === opt;
                    return (
                      <button key={opt} type="button" onClick={() => setSelected(on ? '' : opt)} style={chipStyle(on)}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          };

          const panelContent = filterSubPage ? renderSubPage() : renderMainPage();

          return (
            <>
              <style>{`
                @keyframes srPanelSlideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
                @keyframes srPanelSlideOut { from { transform: translateX(0); } to { transform: translateX(-100%); } }
                @keyframes srFadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes srFadeOut { from { opacity: 1; } to { opacity: 0; } }
              `}</style>
              <div
                onClick={closeFilterSheet}
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 999,
                  animation: isFilterSheetClosing
                    ? `srFadeOut ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
                    : `srFadeIn 0.18s ease-out`
                }}
              />
              <div
                role="dialog" aria-modal="true" aria-label="Filter options"
                style={{
                  position: 'fixed', top: 0, left: 0, bottom: 0,
                  width: '320px', backgroundColor: 'white',
                  zIndex: 1001, overflowY: 'auto',
                  animation: isFilterSheetClosing
                    ? `srPanelSlideOut ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
                    : `srPanelSlideIn ${FILTER_SHEET_ANIM_MS}ms ease-out`
                }}
              >
                {panelContent}
              </div>
            </>
          );
        })()}

        {/* Pedals Grid — same column counts + row-gap as LatestPedals so the
            tiles read as one family across pages. Mobile drops to 2 columns
            (not 1) to match the rest of the app's mobile thumbnail density. */}
        <div className="pedalboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', columnGap: '2rem', rowGap: '2.5rem', maxWidth: '1600px', margin: '0 auto', padding: '0 2rem' }}>
          <style jsx>{`
            @media (max-width: 1200px) {
              .pedalboard-grid {
                grid-template-columns: repeat(3, 1fr) !important;
              }
            }
            @media (max-width: 900px) {
              .pedalboard-grid {
                grid-template-columns: repeat(2, 1fr) !important;
              }
            }
            @media (max-width: 768px) {
              .pedalboard-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                max-width: 100% !important;
                padding: 0 0.5rem !important;
                column-gap: 0.75rem !important;
                row-gap: 2.5rem !important;
              }
            }
          `}</style>
          
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} style={{ 
                borderRadius: '12px', 
                padding: '1.5rem',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </div>
                <div className="h-48 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : displayedResults.length === 0 ? (
            <div className="w-full text-center py-12" style={{ gridColumn: '1 / -1' }}>
              <p className="text-gray-500 text-lg mb-2">No pedals found</p>
              <p className="text-gray-400 text-sm">
                Try adjusting your search or filters.
              </p>
            </div>
          ) : (
            displayedResults.map(pedal => (
              <PedalCard key={pedal.id} pedal={pedal} currentUser={currentUser} firestoreUser={firestoreUser} myWants={myWants} onNavigate={saveScrollState} />
            ))
          )}
        </div>

        {!loading && hasMore && displayedResults.length > 0 && (
          <div ref={sentinelRef} aria-hidden="true" style={{ height: '1px', width: '100%' }} />
        )}

        {/* Load More — fallback when IntersectionObserver is not supported */}
        {hasMore && !loading && !SUPPORTS_INTERSECTION_OBSERVER && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3rem', marginBottom: '3rem' }}>
            <button
              onClick={loadMoreResults}
              style={{
                backgroundColor: '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '12px 32px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#B89020';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#C9A227';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Unified pedal card — same shape as LatestPedals / Recommendations / Want
// List Matches: white pane below the image with uppercase brand + bold model
// + price/condition pill row. No "Wants your X" or "Potential Match" banners
// here (search results are not match-relationship based). Location is also
// no longer rendered — it lives on the listing detail page; surfacing it on
// every tile created visual clutter without driving meaningful decisions.
const PedalCard = ({ pedal, currentUser, firestoreUser, myWants, onNavigate }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Build image array in same order as PedalDetails page
  // Real photos, or exactly one placeholder entry for a photo-less listing
  // (src/utils/listingPlaceholder.js).
  const images = listingImageUrls(pedal);
  const showPlaceholderTag = isPlaceholderListing(pedal);

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Link
      to={`/listing/${pedal.id}`}
      onClick={onNavigate}
      style={{
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'block',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
        border: '1px solid #e5e7eb',
        color: 'inherit'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 6px 14px rgba(0, 0, 0, 0.10), 0 2px 4px rgba(0, 0, 0, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)';
      }}
    >
      {/* Image - full width, sits at top of card with carousel and hover overlay */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="bg-gray-100 relative" style={{ borderRadius: '15px', overflow: 'hidden', width: '100%', height: 'auto', aspectRatio: '1', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {images.length > 0 ? (
            <>
              <ListingThumb
                src={images[currentImageIndex]}
                fallbackSrc={SKETCH_PLACEHOLDER_URL}
                alt={`${pedal.make} ${pedal.model}`}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '15px', display: 'block' }}
                key={currentImageIndex}
              />

              {/* Navigation Arrows + hover description overlay — same chrome as
                  the other pages' cards. Description overlay only renders on
                  desktop (suppressed via media query on touch devices). */}
              <style jsx>{`
                .nav-arrow {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  background: rgba(255, 255, 255, 0.82);
                  border: none;
                  color: #000000;
                  font-size: 26px;
                  cursor: pointer;
                  width: 38px;
                  height: 38px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  z-index: 2;
                  opacity: 0;
                  transition: opacity 0.2s, background 0.2s;
                  user-select: none;
                }

                .nav-arrow-left {
                  left: 6px;
                  padding-bottom: 3px;
                }

                .nav-arrow-right {
                  right: 6px;
                  padding-bottom: 3px;
                }

                .nav-arrow:hover {
                  background: rgba(255, 255, 255, 0.96);
                }

                @media (hover: hover) and (pointer: fine) {
                  .image-container:hover .nav-arrow {
                    opacity: 1;
                  }
                }

                @media (hover: none) or (pointer: coarse) {
                  .nav-arrow {
                    opacity: 0.9;
                  }
                }

                .description-overlay {
                  position: absolute;
                  bottom: 8px;
                  left: 8px;
                  right: 8px;
                  background: rgba(31, 41, 55, 0.80);
                  padding: 12px 16px;
                  transform: translateY(calc(100% + 24px));
                  transition: transform 0.25s ease-in-out;
                  display: flex;
                  align-items: flex-start;
                  z-index: 3;
                  border-radius: 15px;
                  will-change: transform;
                }

                @media (hover: hover) and (pointer: fine) {
                  .image-container:hover .description-overlay {
                    transform: translateY(0);
                  }
                }

                @media (hover: none), (pointer: coarse) {
                  .description-overlay {
                    display: none !important;
                  }
                }

                .description-text {
                  color: #ffffff;
                  font-size: 14px;
                  font-weight: bold;
                  line-height: 1.4;
                  margin: 0;
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  line-clamp: 3;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  max-height: calc(1.4em * 3);
                  width: 100%;
                }
              `}</style>

              <div className="image-container" style={{ position: 'absolute', inset: 0 }}>
                <button
                  className="nav-arrow nav-arrow-left"
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                  style={{ pointerEvents: 'auto' }}
                >
                  ‹
                </button>
                <button
                  className="nav-arrow nav-arrow-right"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  style={{ pointerEvents: 'auto' }}
                >
                  ›
                </button>

                {/* Description Overlay - Desktop Only, First Image Only */}
                {pedal.description && currentImageIndex === 0 && (
                  <div className="description-overlay">
                    <p className="description-text">{pedal.description}</p>
                  </div>
                )}
              </div>

              {/* Active Trader Badge — top-right corner, first image only */}
              {currentImageIndex === 0 && <ActiveTraderBadge user={pedal.owner} />}
              {currentImageIndex === 0 && showPlaceholderTag && (
                <div
                  title="The owner listed this pedal without photos. Real photos are required before an offer can be accepted."
                  style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '13px', fontWeight: 700, letterSpacing: '0.3px', padding: '6px 12px', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10, pointerEvents: 'none' }}
                >
                  Photos coming
                </div>
              )}

              {/* Demand heart — bottom-right (WANT_DEMAND_BADGE_PLAN.md) */}
              {currentImageIndex === 0 && (
                <WantDemandBadge
                  pedal={pedal}
                  currentUser={currentUser}
                  firestoreUser={firestoreUser}
                  myWants={myWants}
                />
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={placeholderFor(pedal).url}
                alt="Photos coming"
                className="w-full h-full"
                style={{ objectFit: 'cover', opacity: 0.85 }}
                style={{ borderRadius: '15px !important' }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Info section — brand (small uppercase), model (bold), price + condition pill row.
          Mirrors the LatestPedals / Recommendations / Wantlist Matches card so all
          four pages read as one card family. Font sizes shrink on mobile to keep
          everything single-line at 2-column tile widths. */}
      <div className="search-pedal-card-info" style={{ backgroundColor: '#ffffff', padding: '14px 16px 16px', textAlign: 'left' }}>
        <style jsx>{`
          .search-pedal-card-brand { font-size: 0.8125rem; }
          .search-pedal-card-model { font-size: 1.25rem; }
          .search-pedal-card-price { font-size: 1.25rem; }
          .search-pedal-card-pill { font-size: 0.8125rem; }
          @media (max-width: 768px) {
            .search-pedal-card-brand { font-size: 0.75rem; }
            .search-pedal-card-model { font-size: 1.0625rem; }
            .search-pedal-card-price { font-size: 1.0625rem; }
            .search-pedal-card-pill { font-size: 0.75rem; }
          }
        `}</style>
        <div title={pedal.make} className="search-pedal-card-brand" style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>
          {pedal.make}
        </div>
        <div title={pedal.model} className="search-pedal-card-model" style={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '8px' }}>
          {pedal.model}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <PedalPriceDisplay
            price={Number(pedal.price)}
            previousPrice={pedal.previousPrice != null ? Number(pedal.previousPrice) : null}
            layout="row"
            className="search-pedal-card-price"
          offerMode={offerModeOf(pedal)}
          />
          <span className="search-pedal-card-pill" style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '9999px', backgroundColor: '#F3F4F6', color: '#374151', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {pedal.condition}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default SearchResults; 